<template>
  <v-dialog
    v-model="isOpen"
    persistent
    scrollable
    fullscreen
    content-class="image-browser sideBySideBrowser">
    <v-card>
      <v-layout column class="escalon-absolute-cover">
        <v-flex shrink>
          <side-by-side-browser-header @closeSideBySide="closeSideBySide" />
        </v-flex>
        <v-flex shrink v-if="sideBySideDisplayAnnotation">
          <side-by-side-browser-annotation-bar
            :leftZoomControlSize="leftZoomControlSize"
            :leftZoomControlFit="leftZoomControlFit"
            :leftTransform="leftTransform"
            :rightZoomControlSize="rightZoomControlSize"
            :rightZoomControlFit="rightZoomControlFit"
            :rightTransform="rightTransform"
            @updateZoom="updateZoom"
            @zoomToFit="zoomToFit"
            @updateCenter="updateCenter"/>
        </v-flex>
        <v-flex shrink v-if="sideBySideDisplayAnnotation">
          <side-by-side-eye-bar />
        </v-flex>
        <v-flex shrink v-if="!sideBySideDisplayAnnotation">
          <side-by-side-browser-toolbar />
        </v-flex>
        <v-flex class="tall-content-wrapper side-by-side-body-wrapper">
          <side-by-side-browser-body
            :leftLargeViewSize="leftLargeViewSize"
            :leftLargeViewFit="leftLargeViewFit"
            :leftTransform="leftTransform"
            :leftImgOffset="leftImgOffset"
            :rightLargeViewSize="rightLargeViewSize"
            :rightLargeViewFit="rightLargeViewFit"
            :rightTransform="rightTransform"
            :rightImgOffset="rightImgOffset"
            :isInBounds="isInBounds"
            :realLocation="realLocation"
            @panBy="panBy"
            @updateZoom="updateZoom"
            @updateCenter="updateCenter"/>
            <side-by-side-image-drawer orientation="left" :context="sideBySideLeftContext" />
            <side-by-side-image-drawer orientation="right" :context="sideBySideRightContext" />
        </v-flex>
      </v-layout>
    </v-card>
  </v-dialog>
</template>

<script>
// imports
import { mapGetters, mapActions } from "vuex";
import sideBySideBrowserHeader from "./SideBySideBrowserHeader";
import sideBySideBrowserAnnotationBar from "./SideBySideBrowserAnnotationBar";
import sideBySideEyeBar from "./SideBySideEyeBar";
import sideBySideBrowserToolbar from "./SideBySideBrowserToolbar";
import sideBySideBrowserBody from "./SideBySideBrowserBody";
import responsiveCanvases from "../mixins/ResponsiveCanvases";
import sideBySideImageDrawer from "./SideBySideImageDrawer";
import { mapWatchers } from "../../helpers/utils";
import {
  defaultSize,
  getViewSize,
  setSize,
  setFittedSize,
  setFitZoom,
  setOffsets,
  defaultTransform,
  setZoom,
  setCenter,
  viewOffset,
  withinViewBounds,
  calculateRealLocation,
  setTransformSizes
} from "../../canvas/geometry";

// exports
export default {
  name: "SideBySideBrowser",
  components: {
    sideBySideBrowserHeader,
    sideBySideBrowserAnnotationBar,
    sideBySideBrowserToolbar,
    sideBySideBrowserBody,
    sideBySideImageDrawer,
    sideBySideEyeBar
  },
  mixins: [responsiveCanvases],
  data() {
    return {
      leftZoomControlSize: defaultSize(),
      leftZoomControlFit: defaultSize(),
      leftLargeViewSize: defaultSize(),
      leftLargeViewFit: defaultSize(),
      rightZoomControlSize: defaultSize(),
      rightZoomControlFit: defaultSize(),
      rightLargeViewSize: defaultSize(),
      rightLargeViewFit: defaultSize(),
      mainWrapperId: "side-by-side-image",
      zoomWrapperId: "zoom-canvas-wrapper",
      leftTransform: defaultTransform(),
      rightTransform: defaultTransform(),
      leftUseFitZoom: true,
      rightUseFitZoom: true
    };
  },
  computed: {
    ...mapGetters([
      "sideBySideContext",
      "sideBySideDisplayAnnotation",
      "sideBySideLeftContext",
      "sideBySideRightContext",
      "sideBySideIsSynced"
    ]),
    isOpen() {
      return !!this.sideBySideContext;
    },
    leftImgOffset() {
      return viewOffset(this.leftLargeViewSize, this.leftLargeViewFit);
    },
    rightImgOffset() {
      return viewOffset(this.rightLargeViewSize, this.rightLargeViewFit);
    },
    leftImage() {
      return this.sideBySideLeftContext && this.sideBySideLeftContext.image;
    },
    leftImgSize() {
      return this.leftImage && this.leftImage.imgSize;
    },
    leftThumbSize() {
      return this.leftImage && this.leftImage.thumbSize;
    },
    leftDisplayImgSize() {
      if (this.leftImgSize && this.leftImgSize.w && this.leftImgSize.h)
        return this.leftImgSize;
      return this.leftThumbSize;
    },
    leftMaxZoom() {
      return this.leftTransform.maxZoom;
    },
    rightImage() {
      return this.sideBySideRightContext && this.sideBySideRightContext.image;
    },
    rightImgSize() {
      return this.rightImage && this.rightImage.imgSize;
    },
    rightThumbSize() {
      return this.rightImage && this.rightImage.thumbSize;
    },
    rightDisplayImgSize() {
      if (this.rightImgSize && this.rightImgSize.w && this.rightImgSize.h)
        return this.rightImgSize;
      return this.rightThumbSize;
    },
    rightMaxZoom() {
      return this.rightTransform.maxZoom;
    },
  },
  methods: {
    ...mapActions(["closeSideBySide"]),
    updateCanvasSizes() {
      this.updateLeftView();
      this.updateRightView();
    },
    updateLeftView() {
      this._updateCanvasSizes(
        this.leftDisplayImgSize,
        this.leftLargeViewSize,
        this.leftLargeViewFit,
        this.leftZoomControlSize,
        this.leftZoomControlFit,
        this.leftTransform,
        this.leftUseFitZoom,
        this.sideBySideLeftContext
      );
    },
    updateRightView() {
      this._updateCanvasSizes(
        this.rightDisplayImgSize,
        this.rightLargeViewSize,
        this.rightLargeViewFit,
        this.rightZoomControlSize,
        this.rightZoomControlFit,
        this.rightTransform,
        this.rightUseFitZoom,
        this.sideBySideRightContext
      );
    },
    _updateCanvasSizes(
      imgSize,
      largeViewSize,
      largeViewFit,
      zoomControlSize,
      zoomControlFit,
      transform,
      useFitZoom,
      context
    ) {
      if (!imgSize) return;

      let mvs = getViewSize(this.mainWrapperId);
      setSize(largeViewSize, mvs);
      setSize(largeViewFit, mvs);

      if (this.sideBySideDisplayAnnotation) {
        let zcs = getViewSize(this.zoomWrapperId);
        setSize(zoomControlSize, zcs);
        setFittedSize(zoomControlSize, imgSize, zoomControlFit, 0);
      }

      // set sizes needed to fit images into all containers
      let lvFit = setFittedSize(largeViewSize, imgSize, largeViewFit, 5, false);

      // set default transform values
      setTransformSizes(transform, largeViewFit, imgSize);
      setFitZoom(transform, lvFit.zoom, useFitZoom);
      setOffsets(transform);

      this._updateContextTransform(context, transform);
    },
    updateZoom(isLeft, zoom) {
      if (isLeft || this.sideBySideIsSynced) {
        this._updateZoom(
          zoom,
          "leftUseFitZoom",
          this.leftTransform,
          this.sideBySideLeftContext
        );
      }
      if (!isLeft || this.sideBySideIsSynced) {
        this._updateZoom(
          zoom,
          "rightUseFitZoom",
          this.rightTransform,
          this.sideBySideRightContext
        );
      }
    },
    _updateZoom(zoom, fitZoomKey, transform, context) {
      this[fitZoomKey] = false;
      setZoom(transform, zoom);
      setOffsets(transform);
      this._updateContextTransform(context, transform);
    },
    updateCenter(isLeft, center) {
      if (isLeft || this.sideBySideIsSynced) {
        this._updateCenter(
          center,
          "leftUseFitZoom",
          this.leftTransform,
          this.leftDisplayImgSize,
          this.leftLargeViewFit
        );
      }
      if (!isLeft || this.sideBySideIsSynced) {
        this._updateCenter(
          center,
          "rightUseFitZoom",
          this.rightTransform,
          this.rightDisplayImgSize,
          this.rightLargeViewFit
        );
      }
    },
    _updateCenter(center, fitZoomKey, transform) {
      this[fitZoomKey] = false;
      setCenter(transform, center.x, center.y);
      setOffsets(transform);
    },
    zoomToFit(isLeft) {
      if (isLeft || this.sideBySideIsSynced) {
        this._zoomToFit(
          "leftUseFitZoom",
          this.leftTransform,
          this.sideBySideLeftContext
        );
      }
      if (!isLeft || this.sideBySideIsSynced) {
        this._zoomToFit(
          "rightUseFitZoom",
          this.rightTransform,
          this.sideBySideRightContext
        );
      }
    },
    _zoomToFit(fitZoomKey, transform, context) {
      this[fitZoomKey] = true;
      setZoom(transform, transform.fitZoom);
      setOffsets(transform);
      this._updateContextTransform(context, transform);
    },
    panBy(isLeft, pan) {
      if (isLeft || this.sideBySideIsSynced) {
        this._panBy(
          pan,
          this.leftTransform,
          this.leftDisplayImgSize,
          "leftUseFitZoom",
          this.leftLargeViewFit
        );
      } 
      if (!isLeft || this.sideBySideIsSynced) {
        this._panBy(
          pan,
          this.rightTransform,
          this.rightDisplayImgSize,
          "rightUseFitZoom",
          this.rightLargeViewFit
        );
      }
    },
    _panBy(pan, transform, imgSize, fitZoomKey, largeViewFit) {
      let center = {
        x: transform.centerX + pan.dx / imgSize.w,
        y: transform.centerY + pan.dy / imgSize.h
      };
      this._updateCenter(center, fitZoomKey, transform, imgSize, largeViewFit);
    },
    resetTransform() {
      this._resetTransform("leftTransform", "leftUseFitZoom", "sideBySideLeftContext");
      this._resetTransform("rightTransform", "rightUseFitZoom", "sideBySideRightContext");
    },
    _resetTransform(transformKey, fitZoomKey, contextKey) {
      this[transformKey] = defaultTransform();
      if (this[contextKey] && this[contextKey].defaultZoom) 
        this[transformKey].defaultZoom = this[contextKey].defaultZoom;
      this[fitZoomKey] = true;
    },
    realLocation(isLeft, x, y) {
      if (isLeft)
        return this._realLocation(
          x,
          y,
          this.leftLargeViewSize,
          this.leftImgOffset,
          this.leftTransform
        );
      return this._realLocation(
        x,
        y,
        this.rightLargeViewSize,
        this.rightImgOffset,
        this.rightTransform
      );
    },
    _realLocation(x, y, viewSize, imgOffset, transform) {
      if (!withinViewBounds(x, y, viewSize, imgOffset)) return { x: -1, y: -1 };
      return calculateRealLocation(x, y, imgOffset, transform);
    },
    isInBounds(isLeft, x, y) {
      if (isLeft) return this._isInBounds(x, y, this.leftDisplayImgSize);
      return this._isInBounds(x, y, this.rightDisplayImgSize);
    },
    _isInBounds(x, y, imgSize) {
      return x >= 0 && y >= 0 && x <= imgSize.w && y <= imgSize.h;
    },
    _updateContextTransform(context, transform) {
      context.setTransform(transform);
      context.annotations.forEach(e => e.setParentTransform(transform));
    }
  },
  watch: {
    ...mapWatchers(["isOpen"], function() {
      this.resetTransform();
      this.resetView();
      this.updateCanvasSizes();
    }),
    // Do not reset transforms when toggling annotation bar to preserve zoom.
    ...mapWatchers(["sideBySideDisplayAnnotation"], function() {
      this.resetView();
      this.updateCanvasSizes();
    }),
    // todo - split up into two instances of the same component components
    ...mapWatchers(["leftDisplayImgSize"], function() {
      this._resetTransform("leftTransform", "leftUseFitZoom", "sideBySideLeftContext");
      this.updateLeftView();
    }),
    ...mapWatchers(["rightDisplayImgSize"], function() {
      this._resetTransform("rightTransform", "rightUseFitZoom", "sideBySideRightContext");
      this.updateRightView();
    }),
    ...mapWatchers(["leftMaxZoom"], function() {
      if (this.sideBySideLeftContext && this.sideBySideLeftContext.defaultZoom) 
        this.leftTransform.defaultZoom = this.sideBySideLeftContext.defaultZoom;
      this.updateLeftView();
    }),
    ...mapWatchers(["rightMaxZoom"], function() {
      if (this.sideBySideRightContext && this.sideBySideRightContext.defaultZoom) 
        this.rightTransform.defaultZoom = this.sideBySideRightContext.defaultZoom;
      this.updateRightView();
    }),
  },
  mounted() {
    this.resetTransform();
    this.updateCanvasSizes();
  }
};
</script>